<script setup>
import { useToast } from 'vue-toastification';
import { useOptionsStore } from '@/stores/options';
import { storeToRefs } from 'pinia';
import { useForm } from '@inertiajs/vue3';
import { useProgramStore } from '@/stores/program';
import { onMounted } from 'vue';

const userStore = useProgramStore();
const { update } = userStore;

const props = defineProps({
    program: {
        type: Object,
        default: () => ({
            lender_id: null,
            program_name: '',
            program_code: '',
            vehicle_min_year: null,
            vehicle_max_year: null,
            effective_date: null,
            end_date: null,
            is_active: true,
            terms: []
        })
    }
});

onMounted(() => {
    if (!programForm.terms || programForm.terms.length === 0) {
        addTerm();
    }
});

const toast = useToast();
const optionsStore = useOptionsStore();
const { lenderOptions, yearOptions, comparisonBandTierOptions, termMonthsOptions } = storeToRefs(optionsStore);
const programForm = useForm({
    ...props.program,
    effective_date: props.program.effective_date ? new Date(props.program.effective_date) : null,
    end_date: props.program.end_date ? new Date(props.program.end_date) : null,
});

const getAvailableTermMonthsOptions = (currentTermIdx) => {
    const options = termMonthsOptions?.value ?? termMonthsOptions ?? [];
    const usedIds = programForm.terms
        .map((t, i) => (i !== currentTermIdx ? t.term_month_id : null))
        .filter(Boolean);
    return options.filter(opt => !usedIds.includes(opt.value));
};

const getMissingTermRatesForTier = (tier, term) => {
    const termRates = (term.term_rates ?? []).filter(tr => tr.rate != null && tr.rate !== '');
    const tierRateValues = (tier.rates ?? []).map(r => r.rate);
    return termRates.filter(tr => !tierRateValues.includes(tr.rate));
};

const saveProgram = async () => {
    try {
        await update({
            ...programForm,
        });
        toast.success('Program Updated');
    } catch (error) {
        toast.error(error.response.data.message || 'An unexpected error occurred');
    }
};

const addTerm = () => {
    const order = programForm.terms.length + 1;
    programForm.terms.push({
        term_month_id: null,
        order: order,
        term_rates: [],
        tiers: []
    });
};

const addTermRate = (term) => {
    const rates = term.term_rates ?? [];
    rates.push({
        rate: null,
        order: rates.length + 1
    });
    term.term_rates = rates;
};

const normalizeRateValue = (rate) => {
    if (rate == null || rate === '') {
        return null;
    }

    const numericRate = Number(rate);
    if (!Number.isNaN(numericRate)) {
        return numericRate;
    }

    return String(rate).trim();
};

const removeTermRate = (term, rateIdx) => {
    const removedTermRate = term.term_rates?.[rateIdx];
    if (!removedTermRate) {
        return;
    }

    const removedRateValue = normalizeRateValue(removedTermRate.rate);

    (term.tiers ?? []).forEach((tier) => {
        const filteredRates = (tier.rates ?? []).filter((tierRate) =>
            normalizeRateValue(tierRate.rate) !== removedRateValue
        );

        tier.rates = filteredRates.map((tierRate, idx) => ({
            ...tierRate,
            order: idx + 1
        }));
    });

    term.term_rates.splice(rateIdx, 1);
    term.term_rates.forEach((rate, idx) => {
        rate.order = idx + 1;
    });
};

const duplicateTerm = (term, tIdx) => {
    if (programForm.terms.length === 0) {
        addTerm();
        return;
    }

    const copiedTerm = programForm.terms[tIdx];
    const newTerm = JSON.parse(JSON.stringify(copiedTerm));
    delete newTerm.term_month_id;

    if (newTerm.id) {
        delete newTerm.id;
        for (const tr of (newTerm.term_rates ?? [])) {
            delete tr.id;
        }
        for (const tier of newTerm.tiers ?? []) {
            if (tier.id) {
                delete tier.id;
            }
            for (const rate of tier.rates ?? []) {
                if (rate.id) {
                    delete rate.id;
                }
            }
        }
    }
    newTerm.order = programForm.terms.length + 1;
    programForm.terms.push(newTerm);
};

const removeTerm = (term, idx) => {
    programForm.terms.forEach((t, i) => {
        if (i > idx) {
            t.order -= 1;
        }
    });

    programForm.terms.splice(idx, 1);
};

const addTier = (term) => {
    const termRates = (term.term_rates ?? []).filter(tr => tr.rate != null && tr.rate !== '');
    const rates = termRates.map((tr, i) => ({
        rate: tr.rate,
        reserve_percent: 0,
        bonus_amount: null,
        order: i + 1
    }));
    term.tiers.push({
        comparison_band_tier_id: null,
        order: (term.tiers ?? []).length + 1,
        rates
    });
};

const duplicateTier = (term, tierIdx) => {
    if (!term.tiers || term.tiers.length === 0) {
        addTier(term);
        return;
    }

    const copiedTier = term.tiers[tierIdx];
    const newTier = JSON.parse(JSON.stringify(copiedTier));
    if (newTier.id) {
        delete newTier.id;
        delete newTier.comparison_band_tier_id;
        for (const rate of newTier.rates ?? []) {
            if (rate.id) {
                delete rate.id;
            }
        }
    }
    newTier.order = term.tiers.length + 1;
    term.tiers.push(newTier);
};

const removeTier = (term, tierIdx) => {
    term.tiers.forEach((tier, i) => {
        if (i > tierIdx) {
            tier.order -= 1;
        }
    });

    term.tiers.splice(tierIdx, 1);
};

const addRateFromTermRate = (tier, term, termRate) => {
    if (!tier.rates) {
        tier.rates = [];
    }
    tier.rates.push({
        rate: termRate.rate,
        reserve_percent: 0,
        bonus_amount: null,
        order: tier.rates.length + 1
    });
};

const removeRate = (tier, rateIdx) => {
    tier.rates.forEach((rate, i) => {
        if (i > rateIdx) {
            rate.order -= 1;
        }
    });

    tier.rates.splice(rateIdx, 1);
};

const getAvailableComparisonBandTierOptions = (term, currentTierIdx) => {
    const options = comparisonBandTierOptions?.value ?? [];
    const tiers = term.tiers ?? [];
    const usedIds = tiers
        .map((t, i) => (i !== currentTierIdx && t.comparison_band_tier_id ? t.comparison_band_tier_id : null))
        .filter(Boolean);
    return options.filter(opt => !usedIds.includes(opt.value));
};

const getComparisonBandValueDisplay = (id) => {
    const options = comparisonBandTierOptions?.value ?? comparisonBandTierOptions ?? [];
    const cbt = options.find(t => t.value === id);
    return cbt?.comparative_value != null
        ? '$' + Number(cbt.comparative_value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : null;
};
</script>

<template>
<MainContentLayout>
    <template #breadcrumb>
        <BreadCrumb />
    </template>
    <template #title>{{ $t('label.program_configuration') }}</template>
    <template #actions>
        <StyledButton @click="saveProgram">
            <FontAwesomeIcon icon="plus" class="mr-1" />
            {{ $t('label.save') }}
        </StyledButton>
    </template>
    <div class="max-w-4xl p-6 space-y-8">
        <!-- Program basic info -->
        <div class="grid md:grid-cols-2 gap-x-6 gap-y-4">
            <StyledDropdown
                v-model="programForm.lender_id"
                :options="lenderOptions"
                name="lender_id"
                :placeholder="$t('label.lender')"
                :label="$t('label.lender')" />
            <StyledInput
                v-model="programForm.program_name"
                name="program_name"
                :placeholder="$t('label.program_name')"
                :label="$t('label.program_name')" />
            <StyledInput
                v-model="programForm.program_code"
                name="program_code"
                :placeholder="$t('label.program_code')"
                :label="$t('label.program_code')" />
            <StyledDropdown
                v-model="programForm.vehicle_min_year"
                :options="yearOptions"
                name="vehicle_min_year"
                :placeholder="$t('label.vehicle_min_year')"
                :label="$t('label.vehicle_min_year')"
                type="number" />
            <StyledDropdown
                v-model="programForm.vehicle_max_year"
                :options="yearOptions"
                name="vehicle_max_year"
                :placeholder="$t('label.vehicle_max_year')"
                :label="$t('label.vehicle_max_year')"
                type="number" />
            <StyledDatepicker
                v-model="programForm.effective_date"
                name="effective_date"
                :label="$t('label.effective_date')"
                :placeholder="$t('label.effective_date')" />
            <StyledDatepicker
                v-model="programForm.end_date"
                name="end_date"
                :label="$t('label.end_date')"
                :placeholder="$t('label.end_date')" />
            <div class="flex items-center">
                <StyledRoundSwitch
                    v-model="programForm.is_active"
                    :label="$t('label.active')"
                    inset
                    name="is_active" />
            </div>
        </div>

        <!-- Terms -->
        <section>
            <div class="flex items-center justify-between mb-4">
                <h2 class="text-lg font-semibold text-gray-800 m-0">{{ $t('label.terms') }}</h2>
                <StyledButton
                    variant="outlined"
                    class="flex items-center gap-2"
                    :label="$t('label.add_term')"
                    @click="addTerm()">
                    <FontAwesomeIcon icon="plus" class="mr-1" />
                    {{ $t('label.add_term') }}
                </StyledButton>
            </div>

            <div
                v-for="(term, tIdx) in programForm.terms"
                :key="tIdx"
                class="relative bg-white rounded-lg border border-grey-400 shadow-sm mb-16 overflow-hidden">
                <!-- Delete term: top-right corner -->
                <button
                    type="button"
                    class="group absolute top-2 right-3 p-2 hover:bg-red-50 rounded transition-colors z-10"
                    :title="$t('label.delete_term')"
                    @click="removeTerm(term, tIdx)">
                    <FontAwesomeIcon icon="trash-alt" class="text-red-500 group-hover:text-red-700 transition-colors" />
                </button>
                <!-- Term header -->
                <div
                    class="px-6 pb-4 pt-10 border-b border-grey-400 bg-grey-100 flex flex-wrap items-center justify-between gap-3">
                    <StyledDropdown
                        v-model="term.term_month_id"
                        class="max-w-[14rem] min-w-[10rem]"
                        :options="getAvailableTermMonthsOptions(tIdx)"
                        :name="`terms.${tIdx}.term_month_id`"
                        :placeholder="$t('label.term_months')"
                        :label="$t('label.term_months')" />
                    <StyledButton
                        variant="outlined"
                        size="small"
                        :label="$t('label.duplicate_term')"
                        @click="duplicateTerm(term, tIdx)">
                        <FontAwesomeIcon icon="plus" />
                        {{ $t('label.duplicate_term') }}
                    </StyledButton>
                </div>

                <div class="p-6 space-y-6">
                    <!-- Term rates -->
                    <div>
                        <div class="flex items-center justify-between mb-3">
                            <h3 class="text-base font-semibold text-gray-700 m-0">{{ $t('label.term_rates') }}</h3>
                            <StyledButton
                                variant="outlined"
                                size="small"
                                :label="$t('label.add_rate')"
                                @click="addTermRate(term)">
                                <FontAwesomeIcon icon="plus" class="mr-1" />
                                {{ $t('label.add_rate') }}
                            </StyledButton>
                        </div>
                        <div
                            v-for="(termRate, trIdx) in (term.term_rates ?? [])"
                            :key="trIdx"
                            class="flex gap-4 items-center mb-3 last:mb-0">
                            <StyledInput
                                v-model="termRate.rate"
                                class="max-w-[14rem] flex-1"
                                :name="`terms.${tIdx}.term_rates.${trIdx}.rate`"
                                :placeholder="$t('label.rate')"
                                :label="$t('label.rate_percent')"
                                type="number" />
                            <button
                                type="button"
                                class="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                :aria-label="$t('label.delete_rate')"
                                @click="removeTermRate(term, trIdx)">
                                <FontAwesomeIcon icon="trash-alt" class="text-red-600" />
                            </button>
                        </div>
                    </div>

                    <!-- Tiers -->
                    <div>
                        <div class="flex items-center justify-between mb-3">
                            <h3 class="text-base font-semibold text-gray-700 m-0">{{ $t('label.tiers') }}</h3>
                            <StyledButton
                                variant="outlined"
                                size="small"
                                :label="$t('label.add_tier')"
                                @click="addTier(term)">
                                <FontAwesomeIcon icon="plus" class="mr-1" />
                                {{ $t('label.add_tier') }}
                            </StyledButton>
                        </div>

                        <div
                            v-for="(tier, tierIdx) in term.tiers"
                            :key="tierIdx"
                            class="relative rounded-lg pt-6 border border-grey-400 bg-gray-50 mb-4 last:mb-0 overflow-hidden">
                            <button
                                type="button"
                                class="group absolute top-2 right-3 p-2 hover:bg-red-50 rounded transition-colors z-10"
                                :title="$t('label.delete_tier')"
                                @click="removeTier(term, tierIdx)">
                                <FontAwesomeIcon icon="trash-alt" class="text-red-500 group-hover:text-red-700 transition-colors" />
                            </button>
                            <div
                                class="px-4 py-3 border-b border-grey-400 flex flex-wrap items-center justify-between gap-3">
                                <div class="flex gap-4 items-center min-w-0 flex-1">
                                    <div class="min-w-0 flex-1 max-w-xs">
                                        <StyledDropdown
                                            v-model="tier.comparison_band_tier_id"
                                            class="max-w-[16rem] -mb-4"
                                            :options="getAvailableComparisonBandTierOptions(term, tierIdx)"
                                            :name="`terms.${tIdx}.tiers.${tierIdx}.comparison_band_tier_id`"
                                            :placeholder="$t('label.comparison_band_label')"
                                            :label="$t('label.comparison_band_label')" />
                                    </div>
                                    <div class="shrink-0 rounded-lg border border-primary-light px-3 py-2 shadow-sm">
                                        <span class="block text-[11px] uppercase tracking-wide text-blue-700/80">
                                            {{ $t('label.comparison_band_value') }}
                                        </span>
                                        <span class="mt-0.5 block text-lg font-bold text-blue-900">
                                            {{ getComparisonBandValueDisplay(tier.comparison_band_tier_id) ?? $t('label.not_set') }}
                                        </span>
                                    </div>
                                </div>
                                <div class="flex gap-3 items-center shrink-0">
                                    <StyledButton
                                        variant="outlined"
                                        size="small"
                                        :label="$t('label.duplicate_tier')"
                                        @click="duplicateTier(term, tierIdx)">
                                        <FontAwesomeIcon icon="plus" class="mr-1" />
                                        {{ $t('label.duplicate_tier') }}
                                    </StyledButton>
                                </div>
                            </div>
                            <div class="p-4">
                                <div class="flex items-center justify-between mb-3 flex-wrap gap-2">
                                    <h4 class="text-sm font-semibold text-gray-600 uppercase tracking-wide m-0">{{
                                        $t('label.rates') }}</h4>
                                    <div
                                        v-if="getMissingTermRatesForTier(tier, term).length > 0"
                                        class="flex gap-2 flex-wrap">
                                        <StyledButton
                                            v-for="tr in getMissingTermRatesForTier(tier, term)"
                                            :key="tr.rate"
                                            variant="outlined"
                                            size="small"
                                            @click="addRateFromTermRate(tier, term, tr)">
                                            <FontAwesomeIcon icon="plus" class="mr-1" />
                                            {{ $t('label.add_rate') }} {{ tr.rate + '%' }}
                                        </StyledButton>
                                    </div>
                                </div>
                                <div
                                    v-for="(rate, rateIdx) in (tier.rates ?? [])"
                                    :key="rateIdx"
                                    class="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center mb-4 last:mb-0 p-3 rounded bg-white border border-grey-300">
                                    <StyledInput
                                        v-model="rate.rate"
                                        :disabled="true"
                                        :name="`terms.${tIdx}.tiers.${tierIdx}.rates.${rateIdx}.rate`"
                                        :placeholder="$t('label.rate')"
                                        :label="$t('label.rate_percent')"
                                        type="number" />
                                    <StyledInput
                                        v-model="rate.reserve_percent"
                                        :name="`terms.${tIdx}.tiers.${tierIdx}.rates.${rateIdx}.reserve_percent`"
                                        :placeholder="$t('label.reserve_percent')"
                                        :label="$t('label.reserve_percent')"
                                        type="number" />
                                    <StyledCurrency
                                        v-model="rate.bonus_amount"
                                        :name="`terms.${tIdx}.tiers.${tierIdx}.rates.${rateIdx}.bonus_amount`"
                                        :placeholder="$t('label.bonus_amount')"
                                        :label="$t('label.bonus_amount')" />
                                    <button
                                        type="button"
                                        class="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors self-center"
                                        :aria-label="$t('label.delete_rate')"
                                        @click="removeRate(tier, rateIdx)">
                                        <FontAwesomeIcon icon="trash-alt" class="text-red-600" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</MainContentLayout>
</template>
